/**
 * Module de gestion des notifications push
 * Gère l'enregistrement du Service Worker, les permissions et l'affichage des notifications
 */

// Clé publique VAPID (pour une vraie implémentation, utiliser une clé générée)
// Pour cette démo, on utilise les notifications locales via Service Worker

const NOTIFICATION_STORAGE_KEY = 'valnexo_push_settings';

/**
 * Vérifie si les notifications sont supportées
 */
export function isPushSupported() {
    return 'Notification' in window && 'serviceWorker' in navigator;
}

/**
 * Récupère les paramètres de notification stockés localement
 */
export function getNotificationSettings() {
    const stored = localStorage.getItem(NOTIFICATION_STORAGE_KEY);
    if (stored) {
        try {
            return JSON.parse(stored);
        } catch {
            return getDefaultSettings();
        }
    }
    return getDefaultSettings();
}

/**
 * Paramètres par défaut - Notifications activées par défaut
 */
function getDefaultSettings() {
    return {
        enabled: true, // Activé par défaut comme demandé
        sound: true,
        showPreview: true,
        channels: {
            chat: true,
            mentions: true,
            reactions: true
        }
    };
}

/**
 * Sauvegarde les paramètres de notification
 */
export function saveNotificationSettings(settings) {
    localStorage.setItem(NOTIFICATION_STORAGE_KEY, JSON.stringify(settings));
}

/**
 * Vérifie si les notifications sont activées
 */
export function areNotificationsEnabled() {
    const settings = getNotificationSettings();
    return settings.enabled && Notification.permission === 'granted';
}

/**
 * Enregistre le Service Worker pour les notifications push
 */
export async function registerServiceWorker() {
    if (!('serviceWorker' in navigator)) {
        console.warn('Service Worker non supporté');
        return null;
    }
    
    try {
        const registration = await navigator.serviceWorker.register('/sw.js', {
            scope: '/'
        });
        console.log('[Push] Service Worker enregistré:', registration.scope);
        return registration;
    } catch (error) {
        console.error('[Push] Erreur enregistrement SW:', error);
        return null;
    }
}

/**
 * Demande la permission pour les notifications
 * @returns {Promise<'granted'|'denied'|'default'>}
 */
export async function requestNotificationPermission() {
    if (!isPushSupported()) {
        return 'denied';
    }
    
    // Si déjà accordé, retourner directement
    if (Notification.permission === 'granted') {
        return 'granted';
    }
    
    // Demander la permission
    try {
        const permission = await Notification.requestPermission();
        return permission;
    } catch (error) {
        console.error('[Push] Erreur demande permission:', error);
        return 'denied';
    }
}

/**
 * Affiche une notification locale
 * @param {Object} options - Options de la notification
 */
export async function showLocalNotification(options) {
    const settings = getNotificationSettings();
    
    // Vérifier si les notifications sont activées
    if (!settings.enabled) {
        console.log('[Push] Notifications désactivées par l\'utilisateur');
        return;
    }
    
    // Vérifier la permission
    if (Notification.permission !== 'granted') {
        console.log('[Push] Permission non accordée');
        return;
    }
    
    // Vérifier si le type de notification est activé
    const channelType = options.channelType || 'chat';
    if (settings.channels && !settings.channels[channelType]) {
        console.log(`[Push] Canal ${channelType} désactivé`);
        return;
    }
    
    try {
        const registration = await navigator.serviceWorker.ready;
        
        const notificationOptions = {
            body: options.body || '',
            icon: options.icon || '/favicon.ico',
            badge: '/favicon.ico',
            tag: options.tag || `notification-${Date.now()}`,
            renotify: true,
            requireInteraction: false,
            silent: !settings.sound,
            data: {
                url: options.url || '/communication',
                ...options.data
            }
        };
        
        // Si preview désactivé, masquer le contenu
        if (!settings.showPreview) {
            notificationOptions.body = 'Nouveau message';
        }
        
        await registration.showNotification(options.title || 'ValNexo', notificationOptions);
        console.log('[Push] Notification affichée:', options.title);
    } catch (error) {
        console.error('[Push] Erreur affichage notification:', error);
        
        // Fallback: notification native directe
        if (Notification.permission === 'granted') {
            new Notification(options.title || 'ValNexo', {
                body: settings.showPreview ? options.body : 'Nouveau message',
                icon: '/favicon.ico',
                silent: !settings.sound
            });
        }
    }
}

/**
 * Initialise le système de notifications push
 * @returns {Promise<boolean>} - true si initialisé avec succès
 */
export async function initializePushNotifications() {
    if (!isPushSupported()) {
        console.warn('[Push] Notifications push non supportées');
        return false;
    }
    
    const settings = getNotificationSettings();
    
    // Si les notifications sont désactivées, ne pas demander de permission
    if (!settings.enabled) {
        console.log('[Push] Notifications désactivées par l\'utilisateur');
        return false;
    }
    
    // Enregistrer le Service Worker
    const registration = await registerServiceWorker();
    if (!registration) {
        return false;
    }
    
    // Si la permission n'est pas encore accordée, la demander
    if (Notification.permission === 'default') {
        const permission = await requestNotificationPermission();
        if (permission !== 'granted') {
            console.log('[Push] Permission refusée par l\'utilisateur');
            return false;
        }
    }
    
    return Notification.permission === 'granted';
}

/**
 * Désactive les notifications
 */
export function disableNotifications() {
    const settings = getNotificationSettings();
    settings.enabled = false;
    saveNotificationSettings(settings);
}

/**
 * Active les notifications
 */
export function enableNotifications() {
    const settings = getNotificationSettings();
    settings.enabled = true;
    saveNotificationSettings(settings);
}
